import { refresh as refreshOnBff, IPortalSession } from "./portalAuthService";
import { AuthenticationKeys } from "../providers/authentication";

// Renovação do token do portal (C4).
//
// O access token do VAULT vale 5 minutos. Sem renovação, a sessão morre nesse
// prazo — era o que acontecia com o handleTokenRefresh stub. São dois gatilhos:
// um relógio que renova antes de expirar e o retry no 401, para o caso de o
// relógio ter perdido a janela (aba em segundo plano, máquina suspensa).
//
// O teto absoluto da sessão é 1 h e não é prorrogável (RN37): estourado, o
// refresh é recusado e o usuário refaz o login.

/** Renova com folga sobre os 5 min de validade do access token. */
const REFRESH_INTERVAL_MS = 4 * 60 * 1000;

let inFlight: Promise<boolean> | null = null;
let timer: ReturnType<typeof setInterval> | null = null;

const readToken = (key: string): string | null => {
  try {
    const raw = localStorage.getItem(key);

    if (!raw) return null;

    return (JSON.parse(raw) as { token?: string } | null)?.token ?? null;
  } catch {
    return null;
  }
};

/**
 * Grava o par novo. O BFF já devolve os tokens no formato que o portal guarda,
 * então não há tradução aqui.
 *
 * O refresh é rotacionado a cada renovação (RN36): manter o antigo faria a
 * tentativa seguinte cair na detecção de reuso e derrubar a sessão inteira.
 */
export const persistSession = (session: IPortalSession): void => {
  localStorage.setItem(AuthenticationKeys.AccessToken, JSON.stringify(session.token));
  localStorage.setItem(AuthenticationKeys.RefreshToken, JSON.stringify(session.refreshToken));
  persistLegacySession(session);
};

/**
 * Guarda o par da API antiga. Vem separado porque o login grava o par do VAULT pelo
 * provider de autenticação, junto com userInfo e empresa selecionada, e só estes
 * dois campos sobram para gravar aqui.
 */
export const persistLegacySession = (session: IPortalSession): void => {
  if (session.legacyToken) {
    localStorage.setItem(
      AuthenticationKeys.LegacyAccessToken, JSON.stringify(session.legacyToken)
    );
  }

  if (session.legacyRefreshToken) {
    localStorage.setItem(
      AuthenticationKeys.LegacyRefreshToken, JSON.stringify(session.legacyRefreshToken)
    );
  }
};

/**
 * Renova o par de tokens. Chamadas concorrentes compartilham a mesma renovação:
 * duas em paralelo consumiriam o mesmo refresh duas vezes, e a segunda seria
 * lida como reuso de token roubado.
 */
export const refreshSession = (): Promise<boolean> => {
  if (inFlight) return inFlight;

  const refreshToken = readToken(AuthenticationKeys.RefreshToken);

  if (!refreshToken) return Promise.resolve(false);

  inFlight = refreshOnBff(refreshToken)
    .then((session) => {
      persistSession(session);
      return true;
    })
    .catch(() => false)
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
};

/** Verdadeiro quando existe sessão para renovar. */
export const hasRefreshToken = (): boolean => readToken(AuthenticationKeys.RefreshToken) !== null;

/** Liga o relógio de renovação. Idempotente. */
export const startSessionKeeper = (): void => {
  if (timer) return;

  timer = setInterval(() => {
    if (readToken(AuthenticationKeys.RefreshToken)) refreshSession();
  }, REFRESH_INTERVAL_MS);
};

export const stopSessionKeeper = (): void => {
  if (!timer) return;

  clearInterval(timer);
  timer = null;
};

export default {
  refreshSession,
  persistSession,
  persistLegacySession,
  hasRefreshToken,
  startSessionKeeper,
  stopSessionKeeper,
};
